/**
 * Which build this is — read once, from the global Vite substitutes into the bundle.
 *
 * The owner deploys from Coolify and had no way to tell, looking at the app, whether the
 * push he just made was the thing on screen. That is the whole job: a sha and a time, in
 * the quietest ink there is, in the bottom-right corner.
 *
 * ## Why this file exists at all rather than reading the global inline
 *
 * `__BUILD_INFO__` is a compile-time substitution, so it exists in the app and in the
 * `client-dom` test project (which extends the client's Vite config) and *nowhere else* —
 * a plain `.test.ts` under the `client` project would find a bare identifier. Funnelling
 * every read through here means one `typeof` guard instead of one per caller, and gives
 * the tests a seam: `setBuildInfo` replaces the answer, so a test can assert the badge
 * renders what it was handed rather than re-deriving today's sha and comparing it to
 * itself.
 */

export interface BuildInfo {
  /** Short commit sha, or `dev` when nothing could resolve one. */
  sha: string;
  /** ISO-8601 instant the bundle was built. */
  builtAt: string;
}

/** What an un-substituted build reports. Not an error state: local tooling hits it. */
const UNKNOWN: BuildInfo = Object.freeze({ sha: 'dev', builtAt: '' });

let override: BuildInfo | null = null;

export function buildInfo(): BuildInfo {
  if (override) return override;
  // `typeof` rather than a plain read: outside the app bundle this identifier does not
  // exist, and touching it directly would throw a ReferenceError.
  if (typeof __BUILD_INFO__ === 'undefined') return UNKNOWN;
  return { sha: __BUILD_INFO__.sha, builtAt: __BUILD_INFO__.builtAt };
}

/** Test seam. Pass `null` to go back to the substituted value. */
export function setBuildInfo(info: BuildInfo | null): void {
  override = info;
}
